// The legacy build is required: the modern pdfjs-dist build uses syntax
// webpack 4 cannot parse. Worker is served from public/pdf.worker.min.js.
import * as pdfjs from "pdfjs-dist/legacy/build/pdf"

let workerConfigured = false

function ensureWorker(): void {
	if (!workerConfigured) {
		pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`
		workerConfigured = true
	}
}

export interface IRasterizedPage {
	canvas: HTMLCanvasElement
	width: number
	height: number
}

export interface IPdfDocumentHandle {
	pageCount: number
	renderPage(pageIndex: number, targetCssWidth: number, scaleFactor?: number): Promise<IRasterizedPage>
	destroy(): void
}

/**
 * Opens PDF bytes with pdfjs and exposes per-page canvas rendering.
 * `targetCssWidth` is the CSS pixel width; rendering happens at
 * devicePixelRatio (or the provided scaleFactor) for sharpness.
 */
export async function openPdf(bytes: Uint8Array): Promise<IPdfDocumentHandle> {
	ensureWorker()
	// pdfjs transfers the buffer to the worker — hand it a copy.
	// isEvalSupported: false — CVE-2024-4367: pdfjs <= 4.1.392 can execute JS
	// from malicious embedded fonts when eval is allowed. Attachment PDFs come
	// from Airtable, so the rendered bytes are not fully trusted.
	const doc = await pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise

	return {
		pageCount: doc.numPages,
		async renderPage(pageIndex: number, targetCssWidth: number, scaleFactor?: number): Promise<IRasterizedPage> {
			const page = await doc.getPage(pageIndex + 1) // pdfjs is 1-based
			const base = page.getViewport({ scale: 1 })
			const dpr = scaleFactor ?? (window.devicePixelRatio || 1)
			const scale = (targetCssWidth / base.width) * dpr
			const viewport = page.getViewport({ scale })

			const canvas = document.createElement("canvas")
			canvas.width = Math.ceil(viewport.width)
			canvas.height = Math.ceil(viewport.height)
			const cssWidth = Math.ceil(viewport.width / dpr)
			const cssHeight = Math.ceil(viewport.height / dpr)
			canvas.style.width = `${cssWidth}px`
			canvas.style.height = `${cssHeight}px`

			const context = canvas.getContext("2d")
			if (!context) {
				throw new Error("Canvas 2d context unavailable")
			}
			await page.render({ canvasContext: context, viewport }).promise
			return { canvas, width: cssWidth, height: cssHeight }
		},
		destroy() {
			doc.destroy()
		},
	}
}

/** One-shot helper: render a single page of a PDF to a data URL (thumbnails). */
export async function pdfPageToDataUrl(bytes: Uint8Array, pageIndex: number, targetCssWidth: number): Promise<string> {
	const handle = await openPdf(bytes)
	try {
		const { canvas } = await handle.renderPage(pageIndex, targetCssWidth)
		return canvas.toDataURL("image/png")
	} finally {
		handle.destroy()
	}
}
